import { FormEvent, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PasswordInput from '@/components/feature/PasswordInput';
import { useMemberAuth } from '@/contexts/MemberAuthContext';
import { MemberApiError, resendVerificationEmail } from '@/services/memberAuth';

const inputClass =
  'w-full px-3.5 md:px-5 py-2.5 md:py-4 rounded-xl border border-[#E8E0D6] text-base outline-none focus:ring-2 focus:ring-[#1B3D2A]/15 focus:border-[#1B3D2A]';

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectPath = searchParams.get('redirect') || '/account';
  const { login } = useMemberAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    setNeedsVerification(false);
    setResendMessage('');
    try {
      await login(email.trim(), password);
      navigate(redirectPath, { replace: true });
    } catch (err) {
      if (err instanceof MemberApiError) {
        setError(err.message);
        setNeedsVerification(Boolean(err.needsVerification));
      } else {
        setError('登入失敗，請稍後再試。');
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResend() {
    if (!email.trim()) {
      setResendMessage('請先輸入 Email。');
      return;
    }
    setResending(true);
    setResendMessage('');
    try {
      await resendVerificationEmail(email.trim());
      setResendMessage('驗證信已重新寄出，請至信箱查收。');
    } catch {
      setResendMessage('發送失敗，請稍後再試。');
    } finally {
      setResending(false);
    }
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-20 md:pt-24">
        <div className="mx-auto max-w-md px-6 py-12 md:py-20">
          <div className="mb-8 text-center">
            <span className="inline-block text-xs font-semibold tracking-widest text-[#D4A574] uppercase mb-2">
              Member Login
            </span>
            <h1 className="text-2xl md:text-3xl font-bold text-[#1B3D2A]">會員登入</h1>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4 rounded-2xl border border-[#E8E0D6] bg-[#FAF8F5] p-6 md:p-8">
            <div>
              <label htmlFor="login-email" className="block text-sm font-medium text-[#1B3D2A] mb-1.5">Email</label>
              <input
                id="login-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@example.com"
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="login-password" className="block text-sm font-medium text-[#1B3D2A] mb-1.5">密碼</label>
              <PasswordInput
                id="login-password"
                required
                value={password}
                onChange={setPassword}
                placeholder="請輸入密碼"
                className={inputClass}
              />
            </div>

            {error && (
              <div className="rounded-xl bg-[#fdebea] px-4 py-3 text-sm text-[#c3423f]">
                {error}
                {needsVerification && (
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={resending}
                    className="ml-2 font-bold underline disabled:opacity-60"
                  >
                    {resending ? '寄送中...' : '重新發送驗證信'}
                  </button>
                )}
              </div>
            )}
            {resendMessage && <p className="text-sm text-[#1B3D2A]">{resendMessage}</p>}

            <button
              type="submit"
              disabled={submitting}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#1B3D2A] py-3.5 text-base font-bold text-white transition-colors hover:bg-[#143026] disabled:opacity-60"
            >
              {submitting ? '登入中...' : '登入'}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-[#5A6B5A]">
            還不是會員嗎？
            <button type="button" onClick={() => navigate(`/register?redirect=${encodeURIComponent(redirectPath)}`)} className="ml-1 font-bold text-[#1B3D2A] hover:underline">
              立即註冊
            </button>
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
